import { Component, Input } from '@angular/core';
import { HouseFlat } from '../../models/house-flat.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StreetBuilding } from '../../models/street-building.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentService } from '../../services/payment.service';
import { MovementService } from '../../services/movement.service';
import { HouseCreditTransactionService } from '../../services/house-credit-transaction.service';
import { ToastService } from '../../services/toast.service';
import { IMovement } from '../../models/movement.model';

@Component({
  selector: 'app-payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrl: './payment-modal.component.scss'
})
export class PaymentModalComponent {

  @Input() streetBuilding?: StreetBuilding;
  @Input() houseFlat?: HouseFlat;
  @Input() neighborhoodId: string = '';

  public formPayment: FormGroup;
  public submitted: boolean = false;

  public selectedFile: File | null = null;

  // Operación asíncrona en curso. Se usa un identificador y no un booleano para
  // que el spinner gire únicamente dentro del botón que se pulsó.
  public savingAction: 'update' | 'credit' | null = null;

  // Opciones para cargos VIEJOS (Payment): null=Sin pago, false=Validando, true=Aprobado
  public options: any[] = [
    { value: null, label: 'Sin pago' },
    { value: false, label: 'Validando' },
    { value: true, label: 'Aprobado' },
  ]

  // Opciones para cargos NUEVOS (Movement): false=Sin pago, null=Pendiente, true=Pagado
  public movementStatusOptions: any[] = [
    { value: false, label: 'Sin pago' },
    { value: 'PENDING', label: 'Pendiente' },
    { value: true, label: 'Pagado' },
  ]

  public movementTypeOptions: any[] = [
    { value: true, label: 'Ingreso' },
    { value: false, label: 'Gasto' },
  ];

  constructor(
    public activeModal: NgbActiveModal,
    public paymentService: PaymentService,
    public movementService: MovementService,
    public houseCreditTransactionService: HouseCreditTransactionService,
    public toastService: ToastService,
    private formBuilder: FormBuilder,
  ) {

    this.formPayment = this.formBuilder.group({
      charge: ['', [Validators.required, Validators.min(0.01)]],
      status: [null],
      notes: [''],
      movementType: [true],
    });

  }

  ngOnInit() {

    const record = this.houseFlat?.payment ?? this.houseFlat?.movement;
    const mv = this.houseFlat?.movement;

    // Para Movement, convertir null (Pendiente) al centinela
    let statusValue: any = record?.status ?? null;
    if (mv && !this.houseFlat?.payment && statusValue === null) {
      statusValue = 'PENDING';
    }

    this.formPayment.patchValue({
      charge: this.houseFlat?.payment?.charge ?? mv?.amount,
      status: statusValue,
      notes: record?.notes ?? '',
      movementType: mv?.movementType ?? true,
    });

    // Un movimiento liquidado en su totalidad con crédito no admite cambios de
    // cantidad, tipo de movimiento ni estatus (el backend responde 400).
    if (this.isPaidWithCredit) {
      this.formPayment.get('charge')?.disable();
      this.formPayment.get('movementType')?.disable();
      this.formPayment.get('status')?.disable();
    }
  }

  get formControls() {
    return this.formPayment.controls;
  }

  /**
   * El cargo es un Movement nuevo y no un Payment legacy.
   */
  get isMovementCharge(): boolean {
    return !!this.houseFlat?.movement && !this.houseFlat?.payment;
  }

  /**
   * El movimiento ya fue pagado en su totalidad con crédito.
   * Misma condición que payment-page.component.html:159.
   */
  get isPaidWithCredit(): boolean {
    if (!this.isMovementCharge) return false;

    const mv = this.houseFlat!.movement!;
    return (mv.creditApplied ?? 0) > 0 && mv.amount === 0;
  }

  /**
   * Hay una operación asíncrona en curso (cualquiera de las dos).
   */
  get saving(): boolean {
    return this.savingAction !== null;
  }

  /**
   * Crédito disponible de la casa. Llega poblado desde GET /charges/{chargeId}.
   */
  get creditBalance(): number {
    return this.houseFlat?.creditBalance ?? 0;
  }

  /**
   * VISIBILIDAD del indicador de crédito. Independiente del botón:
   * se muestra aunque el cargo ya esté pagado o el monto sea 0.
   */
  get hasCredit(): boolean {
    return this.creditBalance > 0;
  }

  /**
   * El crédito alcanza para cubrir el total de la cuota.
   * Gobierna HABILITADO/DESHABILITADO, nunca visibilidad.
   */
  get hasEnoughCredit(): boolean {
    return this.creditBalance >= (this.houseFlat?.movement?.amount ?? 0);
  }

  /**
   * VISIBILIDAD del botón "Pagar con crédito".
   * Se evalúa contra el movement PERSISTIDO, no contra el valor actual del
   * select, para que el botón no aparezca/desaparezca mientras el usuario
   * edita el formulario.
   */
  get showPayWithCredit(): boolean {
    const movement = this.houseFlat?.movement;

    if (!movement || this.houseFlat?.payment) return false;
    if (movement.status !== false) return false;
    if (movement.amount <= 0) return false;

    return this.hasCredit;
  }

  /**
   * Deshabilitado por operación en curso O por crédito insuficiente.
   */
  get payWithCreditDisabled(): boolean {
    return this.saving || !this.hasEnoughCredit;
  }

  /**
   * Cadena vacía = ngbTooltip no abre. Así el tooltip solo explica el caso
   * "crédito insuficiente" y nunca aparece por el disabled de `saving`.
   */
  get payWithCreditTooltip(): string {
    return this.hasEnoughCredit ? '' : 'Crédito insuficiente';
  }

  /**
   * "Actualizar" cede el ancla visual únicamente cuando "Pagar con crédito"
   * está presente Y habilitado.
   */
  get updateButtonClass(): string {
    return this.showPayWithCredit && this.hasEnoughCredit
      ? 'btn-outline-primary'
      : 'btn-primary';
  }

  /*
      ██████╗ ███╗   ██╗███████╗██╗██╗     ███████╗ ██████╗██╗  ██╗ █████╗ ███╗   ██╗ ██████╗ ███████╗
    ██╔═══██╗████╗  ██║██╔════╝██║██║     ██╔════╝██╔════╝██║  ██║██╔══██╗████╗  ██║██╔════╝ ██╔════╝
    ██║   ██║██╔██╗ ██║█████╗  ██║██║     █████╗  ██║     ███████║███████║██╔██╗ ██║██║  ███╗█████╗
    ██║   ██║██║╚██╗██║██╔══╝  ██║██║     ██╔══╝  ██║     ██╔══██║██╔══██║██║╚██╗██║██║   ██║██╔══╝
    ╚██████╔╝██║ ╚████║██║     ██║███████╗███████╗╚██████╗██║  ██║██║  ██║██║ ╚████║╚██████╔╝███████╗
      ╚═════╝ ╚═╝  ╚═══╝╚═╝     ╚═╝╚══════╝╚══════╝ ╚═════╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═══╝ ╚═════╝ ╚══════╝
  
    */
    /**
     * FUNCTION onFileChange
     * 
     * @param event 
     */
    onFileChange(event: any) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
      }
    }

  /*
   ██████╗ ███╗   ██╗███████╗██╗   ██╗██████╗ ███╗   ███╗██╗████████╗
  ██╔═══██╗████╗  ██║██╔════╝██║   ██║██╔══██╗████╗ ████║██║╚══██╔══╝
  ██║   ██║██╔██╗ ██║███████╗██║   ██║██████╔╝██╔████╔██║██║   ██║
  ██║   ██║██║╚██╗██║╚════██║██║   ██║██╔══██╗██║╚██╔╝██║██║   ██║
  ╚██████╔╝██║ ╚████║███████║╚██████╔╝██████╔╝██║ ╚═╝ ██║██║   ██║
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝   ╚═╝

  */
  async onSubmit() {
    this.submitted = true;
    if (this.formPayment.invalid) return;
    if (this.saving) return;

    // getRawValue() SI incluye los controles deshabilitados.
    // formPayment.value los omite y devolvería undefined en charge/status/movementType.
    const rawForm = this.formPayment.getRawValue();

    this.savingAction = 'update';

    try {

      // Cargo nuevo: actualizar Movement directamente
      if (this.houseFlat?.movement && !this.houseFlat?.payment) {
        const mv = this.houseFlat.movement;

        // Campos bloqueados: se envían siempre con el valor original del movimiento.
        const rawStatus: any = this.isPaidWithCredit ? mv.status : rawForm.status;
        const resolvedStatus: boolean | null = rawStatus === 'PENDING' ? null : rawStatus;

        const resolvedMovementType: boolean = this.isPaidWithCredit
          ? mv.movementType
          : rawForm.movementType ?? mv.movementType;

        const resolvedAmount: number = this.isPaidWithCredit
          ? mv.amount
          : rawForm.charge ?? mv.amount;

        const updatedMovement: IMovement = {
          movementId: mv.movementId,
          neighborhoodId: mv.neighborhoodId,
          houseFlatId: mv.houseFlatId,
          streetBuildingId: mv.streetBuildingId,
          description: mv.description,
          notes: rawForm.notes ?? mv.notes,
          movementType: resolvedMovementType,
          status: resolvedStatus,
          amount: resolvedAmount,
          creditApplied: mv.creditApplied,
          createdAt: mv.createdAt instanceof Date
            ? mv.createdAt.toISOString()
            : mv.createdAt as unknown as string,
          updatedAt: mv.updatedAt instanceof Date
            ? mv.updatedAt.toISOString()
            : mv.updatedAt as unknown as string,
        };
        const flag = await this.movementService.update(
          updatedMovement,
          this.selectedFile ?? undefined
        );
        if (flag) this.activeModal.close();
        return;
      }

      // Cargo viejo: usar paymentService como antes
      const flag = await this.paymentService.update(
        this.neighborhoodId,
        this.houseFlat!,
        rawForm,
        this.selectedFile ?? undefined
      );
      if (flag) this.activeModal.close();

    } finally {
      this.savingAction = null;
    }
  }

  /*
   ██████╗██████╗ ███████╗██████╗ ██╗████████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗██║╚══██╔══╝
  ██║     ██████╔╝█████╗  ██║  ██║██║   ██║
  ██║     ██╔══██╗██╔══╝  ██║  ██║██║   ██║
  ╚██████╗██║  ██║███████╗██████╔╝██║   ██║
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═════╝ ╚═╝   ╚═╝

  */
  // region payWithCredit
  async payWithCredit() {

    if (this.saving) return;
    if (!this.showPayWithCredit || !this.hasEnoughCredit) return;

    const movement = this.houseFlat?.movement;
    const streetBuildingId = this.houseFlat?.streetBuildingId
      ?? this.streetBuilding?.streetBuildingId;

    if (!movement || !streetBuildingId || !this.houseFlat) return;

    this.savingAction = 'credit';

    try {

      const result = await this.houseCreditTransactionService.apply(
        this.neighborhoodId,
        streetBuildingId,
        this.houseFlat.houseFlatId,
        movement.movementId,
      );

      if (result) {
        this.activeModal.close();
      }

    } catch (error) {
      console.error(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white',
      });
    } finally {
      this.savingAction = null;
    }

  }

}
